import React, { useEffect, useMemo, useState } from "react";
import Blok from "../../../interfaces/Storyblok/Blok.interface";
import SchoolPicker from "../../molecules/SchoolPicker/SchoolPicker.molecule";
import SuccessCheck from "../../../assets/svgs/SuccessCheck.svg";
import {
  DEFAULT_COMMUNICATIONS_CONSENT_TEXT,
  DEFAULT_CONSENT_TO_PROCESS_TEXT,
  DEFAULT_HEADING,
  DEFAULT_SUBMIT_LABEL,
  DEFAULT_SUBSCRIPTION_TYPE_ID,
  DEFAULT_SUCCESS_MESSAGE,
  FIRST_YEAR_OPTIONS,
  getBrandValue,
} from "./prepForm.constants";
import { captureAndGetUtms, isValidAUMobile, isValidEmail, toE164AU } from "./prepForm.browser";
import { submitPrepForm } from "./submitPrepForm";

interface PrepEnquiryFormProps extends Blok {
  heading?: string;
  intro?: string;
  submit_label?: string;
  success_message?: string;
  consent_text?: string;
  communications_consent_text?: string;
  subscription_type_id?: string;
  portal_id?: string;
  form_guid?: string;
  omit_hutk?: boolean; // shared-device (info-night iPad) placements: don't send hutk
}

type Errors = Partial<Record<string, string>>;

// Fixed colours (not brand tokens) so the form matches the HubSpot embedded form on every brand build.
const FORM_TEXT = "text-[#4a1375]";
const labelClasses = `block text-sm font-bold ${FORM_TEXT} pb-1`;
const fieldClasses = `w-full h-[52px] px-4 rounded-full bg-White text-sm ${FORM_TEXT} placeholder-[#4a1375]/50 border border-[#4a1375]/40 focus:ring-0 focus:outline-none`;

const PrepEnquiryForm: React.FC<PrepEnquiryFormProps> = ({
  heading,
  intro,
  submit_label,
  success_message,
  consent_text,
  communications_consent_text,
  subscription_type_id,
  portal_id,
  form_guid,
  omit_hutk,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [school, setSchool] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [zip, setZip] = useState("");
  const [firstYear, setFirstYear] = useState("");
  const [communicationsConsent, setCommunicationsConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");

  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [utms, setUtms] = useState<ReturnType<typeof captureAndGetUtms>>({});
  useEffect(() => {
    setUtms(captureAndGetUtms());
  }, []);

  // Storyblok sends blank fields as "" (not undefined), so coalesce with || to hit the defaults.
  const headingText = heading?.trim() || DEFAULT_HEADING;
  const submitLabel = submit_label?.trim() || DEFAULT_SUBMIT_LABEL;
  const successMessage = success_message?.trim() || DEFAULT_SUCCESS_MESSAGE;
  const consentToProcessText = consent_text?.trim() || DEFAULT_CONSENT_TO_PROCESS_TEXT;
  const communicationsText = communications_consent_text?.trim() || DEFAULT_COMMUNICATIONS_CONSENT_TEXT;
  const subscriptionTypeId = Number(subscription_type_id) || DEFAULT_SUBSCRIPTION_TYPE_ID;

  const validate = (): Errors => {
    const next: Errors = {};
    if (!firstName.trim()) next.firstName = "Please enter your first name.";
    if (!lastName.trim()) next.lastName = "Please enter your last name.";
    if (!email.trim()) next.email = "Please enter your email.";
    else if (!isValidEmail(email)) next.email = "Please enter a valid email.";
    const e164 = toE164AU(mobile);
    if (!mobile.trim()) next.mobile = "Please enter your mobile number.";
    else if (!isValidAUMobile(e164)) next.mobile = "Please enter a valid Australian mobile number.";
    if (!school.trim() || !serviceId) next.school = "Please select your school from the list.";
    if (!firstYear) next.firstYear = "Please choose the first year of school.";
    return next;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    // Honeypot: a bot filled the hidden field — drop silently.
    if (honeypot.trim()) {
      setSubmitted(true);
      return;
    }

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) return;

    setSubmitting(true);
    const result = await submitPrepForm(
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        mobile: toE164AU(mobile),
        school: school.trim(),
        serviceId,
        brand: getBrandValue(),
        zip: zip.trim() || undefined,
        firstYear,
        utms,
      },
      {
        portalId: portal_id?.trim(),
        formGuid: form_guid?.trim(),
        consentToProcessText,
        communicationsConsentText: communicationsText,
        communicationsConsent,
        subscriptionTypeId,
        omitHutk: omit_hutk,
      },
    );
    setSubmitting(false);

    if (result.ok) {
      setSubmitted(true);
    } else {
      setSubmitError("Sorry, something went wrong submitting your details. Please try again.");
    }
  };

  const errorText = (key: string) =>
    errors[key] ? <p className="mt-1 text-xs text-[#d32f2f]">{errors[key]}</p> : null;

  const yearOptions = useMemo(() => FIRST_YEAR_OPTIONS, []);

  if (submitted) {
    // "Thank you" success state (Figma node 1281:20) — brand-themed to match the brand mock.
    return (
      <div className="max-w-[640px] mx-auto">
        <div className="flex flex-col items-center gap-5 rounded-[24px] bg-White p-12 text-center">
          <SuccessCheck className="h-[50px] w-[50px] shrink-0" />
          <h2 className="font-secondary font-bold text-[33px] leading-[40px] text-Primary-900">{successMessage}</h2>
        </div>
      </div>
    );
  }

  return (
    <form className="max-w-xl mx-auto px-8 py-8 font-secondary" onSubmit={handleSubmit} noValidate>
      <h2 className={`text-2xl font-bold ${FORM_TEXT} mb-2`}>{headingText}</h2>
      {intro && <p className={`text-sm ${FORM_TEXT} mb-6`}>{intro}</p>}

      <div className="grid gap-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="prep-firstname" className={labelClasses}>First name</label>
            <input id="prep-firstname" className={fieldClasses} value={firstName}
              onChange={e => setFirstName(e.target.value)} autoComplete="given-name" />
            {errorText("firstName")}
          </div>
          <div>
            <label htmlFor="prep-lastname" className={labelClasses}>Last name</label>
            <input id="prep-lastname" className={fieldClasses} value={lastName}
              onChange={e => setLastName(e.target.value)} autoComplete="family-name" />
            {errorText("lastName")}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="prep-email" className={labelClasses}>Email</label>
            <input id="prep-email" type="email" className={fieldClasses} value={email}
              onChange={e => setEmail(e.target.value)} autoComplete="email" />
            {errorText("email")}
          </div>
          <div>
            <label htmlFor="prep-mobile" className={labelClasses}>Mobile number</label>
            <input id="prep-mobile" type="tel" className={fieldClasses} value={mobile}
              onChange={e => setMobile(e.target.value)} autoComplete="tel" placeholder="04XX XXX XXX" />
            {errorText("mobile")}
          </div>
        </div>

        <div>
          <SchoolPicker
            label="School"
            placeholder="Start typing your school name, suburb or postcode"
            onSelect={({ school: name, serviceId: id }) => {
              setSchool(name);
              setServiceId(id);
            }}
          />
          {errorText("school")}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="prep-zip" className={labelClasses}>Postcode <span className="font-normal text-[#4a1375]/60">(optional)</span></label>
            <input id="prep-zip" className={fieldClasses} value={zip}
              onChange={e => setZip(e.target.value)} autoComplete="postal-code" inputMode="numeric" />
          </div>
          <div>
            <label htmlFor="prep-firstyear" className={labelClasses}>First year at school</label>
            <div className="relative">
              <select id="prep-firstyear" className={`${fieldClasses} appearance-none pr-12 cursor-pointer`}
                value={firstYear} onChange={e => setFirstYear(e.target.value)}>
                <option value="">Please select…</option>
                {yearOptions.map(year => <option key={year} value={year}>{year}</option>)}
              </select>
              <svg
                className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-[#4a1375]"
                width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"
              >
                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {errorText("firstYear")}
          </div>
        </div>

        <label className={`flex items-start gap-2 text-xs ${FORM_TEXT}`}>
          <input type="checkbox" className="mt-0.5 accent-[#4a1375]" checked={communicationsConsent}
            onChange={e => setCommunicationsConsent(e.target.checked)} />
          <span>{communicationsText}</span>
        </label>
        <p className="text-xs text-[#4a1375]/60">{consentToProcessText}</p>

        {/* Honeypot */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={honeypot}
          onChange={e => setHoneypot(e.target.value)}
          className="hidden"
        />

        {submitError && <p className="text-sm text-[#d32f2f]">{submitError}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="justify-self-center bg-SP3 text-Primary-Button-Text hover:!bg-SP8 font-extrabold uppercase rounded-full px-8 py-4 leading-none transition-background transition-text ease-in-out duration-300 cursor-pointer focus:ring-0 focus:outline-none disabled:opacity-60"
        >
          {submitting ? "Submitting…" : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default PrepEnquiryForm;
